import { Session } from '../types/Session';
import { SessionAnalysisType, SessionAnalysisTypeUtils } from '../types/SessionAnalysisType';

const LOCALE_OPTS: Intl.CollatorOptions = { sensitivity: 'base' };

// Sessions eligible for next/prev navigation: all normal sessions plus the
// freeride singleton. Discarded / archive / reference singletons are never
// reachable via next/prev, and neither are hidden sessions — those are only
// reachable from the session list with "Show hidden" ticked.
// keepId exempts one session (the one being viewed) from the hidden filter, so
// that landing on a hidden session directly still gives it working neighbours.
export function filterNavigableSessions<T extends Session>(sessions: T[], keepId?: string): T[] {
  return sessions.filter(
    (s) => (!SessionAnalysisTypeUtils.isSingletonLevel(s.analysis_type)
      || s.analysis_type === SessionAnalysisType.FREERIDE_UNSTRUCTURED)
      && (!s.hidden || s.id === keepId),
  );
}

export function getSessionAnchorTs(session: Session): number | null {
  let earliest: number | null = null;
  for (const cfg of session.configs ?? []) {
    for (const run of cfg.runs ?? []) {
      if (!run.run_ts) continue;
      const ts = Date.parse(run.run_ts);
      if (Number.isNaN(ts)) continue;
      if (earliest === null || ts < earliest) earliest = ts;
    }
  }
  return earliest;
}

function whenMs(session: Session): number {
  const anchor = getSessionAnchorTs(session);
  if (anchor !== null) return anchor;
  const d = session.date ? Date.parse(session.date) : NaN;
  return Number.isNaN(d) ? 0 : d;
}

function riderName(session: Session, ridersById?: Map<string, any>): string {
  return ridersById?.get(session.rider_id)?.nickname ?? '';
}

export function compareSessionsAsc(
  a: Session,
  b: Session,
  ridersById?: Map<string, any>,
): number {
  const wa = whenMs(a);
  const wb = whenMs(b);
  if (wa !== wb) return wa - wb;

  const ra = riderName(a, ridersById);
  const rb = riderName(b, ridersById);
  const byRider = ra.localeCompare(rb, undefined, LOCALE_OPTS);
  if (byRider !== 0) return byRider;

  const da = a.description ?? '';
  const db = b.description ?? '';
  return da.localeCompare(db, undefined, LOCALE_OPTS);
}

export function compareSessionsDesc(
  a: Session,
  b: Session,
  ridersById?: Map<string, any>,
): number {
  return -compareSessionsAsc(a, b, ridersById);
}
